import { forwardRef, useId } from "react";
import { Input, type InputProps } from "@/components/ui/input";
import { useMetaDepartments } from "@/hooks/useMetaDirectory";

export interface DepartmentComboboxProps
  extends Omit<InputProps, "list" | "type" | "value" | "onChange"> {
  value: string;
  onChange: (value: string) => void;
  /** Fired when the typed/selected value matches a known department —
   *  carries the row so the parent can copy the default cost center
   *  alongside. */
  onMatch?: (department: { name: string; defaultCostCenter: string }) => void;
}

// =============================================================================
// DepartmentCombobox
//
// An <input> backed by a native <datalist> populated from the admin
// directory of departments. Free-text typing still works (the form
// keeps a free-text fallback while the directory is being seeded), and
// when the user picks a known value the component fires onMatch so the
// parent can prefill the cost-center field too.
// =============================================================================

export const DepartmentCombobox = forwardRef<HTMLInputElement, DepartmentComboboxProps>(
  ({ value, onChange, onMatch, ...rest }, ref) => {
    const listId = useId();
    const { data } = useMetaDepartments();
    const items = data?.departments ?? [];

    return (
      <>
        <Input
          ref={ref}
          type="text"
          list={listId}
          autoComplete="off"
          value={value}
          onChange={(e) => {
            const next = e.target.value;
            onChange(next);
            const trimmed = next.trim().toLowerCase();
            if (!trimmed) return;
            const match = items.find((d) => d.name.toLowerCase() === trimmed);
            if (match) onMatch?.(match);
          }}
          {...rest}
        />
        <datalist id={listId}>
          {items.map((d) => {
            // Admin-only entries are filtered server-side for regular
            // users, so they never reach this code path for `user`
            // roles. For admins, the datalist label hints "admin only"
            // inline so they know which entries are sandbox / test
            // (the only visual affordance a native <datalist> gives
            // us — option styling is browser-controlled).
            const adminTag = d.adminOnly ? " · ADMIN ONLY" : "";
            const ccTag = d.defaultCostCenter
              ? `Cost center ${d.defaultCostCenter}`
              : "";
            const label = ccTag + adminTag || (d.adminOnly ? "ADMIN ONLY" : "");
            return (
              <option key={d.id} value={d.name}>
                {label}
              </option>
            );
          })}
        </datalist>
      </>
    );
  },
);
DepartmentCombobox.displayName = "DepartmentCombobox";
